import {
  EXERCISE_SCENARIO_BOUNDS,
  EXERCISE_OP_ALPHA_PROXIMITY_THREAT_VESSEL_ID,
  EXERCISE_OP_ALPHA_PROXIMITY_BUFFER_M
} from '../data/realisticExerciseScenarioData';
import { isVesselNearOpAlphaThreatZone } from './exerciseOpAlphaProximity';
import {
  getExerciseRoleMetaForApiId,
  isKnownExerciseVesselApiId,
  normalizeExerciseVesselApiId,
  resolveExerciseRoleKey
} from './exerciseVesselRoleLookup';

/** Match MapContainer VESSEL_STATUS_RGB.KNOWN — track before threat is classified */
const PRE_THREAT_TRACK_RGBA = [34, 197, 94, 220];
/** Threat-classified segment */
const POST_THREAT_TRACK_RGBA = [239, 68, 68, 220];

function rowTimeMs(row) {
  const v = row.timestamp ?? row.time;
  if (v == null) return NaN;
  const ms = new Date(v).getTime();
  return Number.isNaN(ms) ? NaN : ms;
}

function rowIsThreatDetected(row) {
  if (!row) return false;
  const t = row.threat_detected;
  return t === 'Yes' || t === true || t === 1;
}

/** Canonical role id for the Op Alpha proximity threat (works with REACT_APP_EXERCISE_VESSEL_ROLE_ALIAS_JSON). */
const OP_ALPHA_THREAT_ROLE_NORM = normalizeExerciseVesselApiId(
  resolveExerciseRoleKey(EXERCISE_OP_ALPHA_PROXIMITY_THREAT_VESSEL_ID) || EXERCISE_OP_ALPHA_PROXIMITY_THREAT_VESSEL_ID
);

/**
 * Index of the first point **in the threat (red) segment** — i.e. where detection “starts” on the track.
 * - Prefer a **state change** non-threat → threat on consecutive points (avoids APIs that set threat on all rows retroactively).
 * - Else first point inside/near Op Alpha (scenario geography).
 * - If neither applies in this time window, returns pts.length (entire track stays green).
 * @returns {number} Index in pts (0..length)
 */
function firstThreatTransitionIndex(pts, normalizedRoleKey) {
  if (!pts.length || normalizedRoleKey !== OP_ALPHA_THREAT_ROLE_NORM) return pts.length;

  for (let i = 1; i < pts.length; i++) {
    if (rowIsThreatDetected(pts[i].tooltipRow) && !rowIsThreatDetected(pts[i - 1].tooltipRow)) {
      return i;
    }
  }

  const buf = EXERCISE_OP_ALPHA_PROXIMITY_BUFFER_M;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    if (isVesselNearOpAlphaThreatZone(p.lon, p.lat, buf)) return i;
  }

  return pts.length;
}

/**
 * From normalized rawData rows, build PathLayer + head dot data for known exercise vessel IDs.
 * @param {Array} rawData
 * @param {number|null|undefined} playbackTimeMs - if set, only points at or before this time (story clock)
 * @returns {{ paths: Array, heads: Array }}
 */
export function buildExerciseVesselPathsFromRawData(rawData, playbackTimeMs = null) {
  const paths = [];
  const heads = [];
  if (!rawData || rawData.length === 0) return { paths, heads };

  const groups = new Map();

  for (const row of rawData) {
    const id = normalizeExerciseVesselApiId(row.unique_identifier);
    if (!id || !isKnownExerciseVesselApiId(id)) continue;
    const lat = row.lat ?? row.location_latitude;
    const lon = row.lon ?? row.location_longitude;
    if (lat == null || lon == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lon))) continue;
    const ts = rowTimeMs(row);
    if (Number.isNaN(ts)) continue;
    const latN = Number(lat);
    const lonN = Number(lon);
    const tooltipRow = {
      ...row,
      lat: latN,
      lon: lonN,
      unique_identifier: row.unique_identifier ?? id
    };
    if (!groups.has(id)) groups.set(id, []);
    groups.get(id).push({ lat: latN, lon: lonN, ts, tooltipRow });
  }

  for (const [id, pts] of groups) {
    const meta = getExerciseRoleMetaForApiId(id);
    if (!meta || pts.length === 0) continue;

    pts.sort((a, b) => a.ts - b.ts);
    let usePts = pts;
    if (playbackTimeMs != null && !Number.isNaN(playbackTimeMs)) {
      usePts = pts.filter(p => p.ts <= playbackTimeMs);
    }
    if (usePts.length === 0) continue;

    const last = usePts[usePts.length - 1];
    const tooltipRow = last.tooltipRow;
    heads.push({
      id,
      position: [last.lon, last.lat],
      label: meta.label,
      fill: meta.headFill,
      line: meta.color,
      tooltipRow
    });

    if (usePts.length >= 2) {
      const roleNorm = normalizeExerciseVesselApiId(resolveExerciseRoleKey(id) || id);
      const splitThreatTrack = roleNorm === OP_ALPHA_THREAT_ROLE_NORM;
      if (splitThreatTrack) {
        const ft = firstThreatTransitionIndex(usePts, roleNorm);
        const w = meta.width ?? 3;
        const base = { vesselId: id, label: meta.label, width: w };

        if (ft === 0) {
          paths.push({
            ...base,
            id: `${id}-track-post`,
            path: usePts.map(p => [p.lon, p.lat]),
            color: POST_THREAT_TRACK_RGBA,
            tooltipRow
          });
        } else if (ft >= usePts.length) {
          paths.push({
            ...base,
            id: `${id}-track-pre`,
            path: usePts.map(p => [p.lon, p.lat]),
            color: PRE_THREAT_TRACK_RGBA,
            tooltipRow
          });
        } else {
          if (ft >= 2) {
            const pre = usePts.slice(0, ft);
            paths.push({
              ...base,
              id: `${id}-track-pre`,
              path: pre.map(p => [p.lon, p.lat]),
              color: PRE_THREAT_TRACK_RGBA,
              tooltipRow: pre[pre.length - 1].tooltipRow
            });
          }
          const suffix = usePts.slice(ft - 1);
          if (suffix.length >= 2) {
            paths.push({
              ...base,
              id: `${id}-track-post`,
              path: suffix.map(p => [p.lon, p.lat]),
              color: POST_THREAT_TRACK_RGBA,
              tooltipRow: suffix[suffix.length - 1].tooltipRow
            });
          }
        }
      } else {
        paths.push({
          id,
          vesselId: id,
          path: usePts.map(p => [p.lon, p.lat]),
          color: meta.color,
          width: meta.width ?? 3,
          label: meta.label,
          tooltipRow
        });
      }
    }
  }

  if (
    process.env.NODE_ENV !== 'production' &&
    paths.length === 0 &&
    heads.length === 0 &&
    rawData.length > 0
  ) {
    logExerciseVesselIdHintOnce(rawData);
  }

  return { paths, heads };
}

let lastExerciseHintKey = '';
let lastExerciseHintAt = 0;

function rowInExerciseBounds(lat, lon) {
  const { west, east, south, north } = EXERCISE_SCENARIO_BOUNDS;
  return lon >= west && lon <= east && lat >= south && lat <= north;
}

/** One hint per ~25s: distinct vessel IDs near the exercise box (helps update EXERCISE_VESSEL_ROLES / alias JSON). */
function logExerciseVesselIdHintOnce(rawData) {
  const now = Date.now();
  const key = `${rawData.length}:${rawData[0]?.time ?? ''}`;
  if (key === lastExerciseHintKey && now - lastExerciseHintAt < 25000) return;
  lastExerciseHintKey = key;
  lastExerciseHintAt = now;

  const counts = new Map();
  for (const row of rawData) {
    const id = normalizeExerciseVesselApiId(row.unique_identifier);
    if (!id) continue;
    const lat = row.lat ?? row.location_latitude;
    const lon = row.lon ?? row.location_longitude;
    if (lat == null || lon == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lon))) continue;
    if (!rowInExerciseBounds(Number(lat), Number(lon))) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  const top = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12)
    .map(([uid, n]) => `${uid} (${n} pts)`);
  if (top.length === 0) {
    // eslint-disable-next-line no-console
    console.info(
      '[exercise] No vessel points inside EXERCISE_SCENARIO_BOUNDS; check date range / datasource table UUID (REACT_APP_BLUETIDE_SURFACE_VESSEL_DATASOURCE_UUID).'
    );
    return;
  }
  // eslint-disable-next-line no-console
  console.info(
    '[exercise] No tracks matched EXERCISE_VESSEL_ROLES. Vessel IDs with points in the exercise bounds (update roles or set REACT_APP_EXERCISE_VESSEL_ROLE_ALIAS_JSON):',
    top.join(', ')
  );
}
